<template>
   <div class="card">
      <div class="card-body m-2">

         <!-- Quick Install -->
         <div class="section-card mb-4">
            <h5 class="section-title">
               <i class="fa-solid fa-download me-2"></i> Quick Install
            </h5>
            <p class="section-desc">Install XSurfLog directly from your browser's extension store. Click the browser you use below to get started.</p>

            <div class="browser-grid">
               <a class="browser-card" href="https://chrome.google.com/webstore/detail/xsurf-log/mipgaapolfgfbonecakhhacpmafffogo" target="_blank">
                  <i class="fa-brands fa-chrome browser-icon" style="color: #4285F4;"></i>
                  <span class="browser-name">Google Chrome</span>
                  <span class="browser-sub">Chrome Web Store</span>
               </a>
               <a class="browser-card" href="https://addons.mozilla.org/en-US/firefox/addon/xsurf-log/" target="_blank">
                  <i class="fa-brands fa-firefox-browser browser-icon" style="color: #FF7139;"></i>
                  <span class="browser-name">Mozilla Firefox</span>
                  <span class="browser-sub">Firefox Add-ons</span>
               </a>
               <a class="browser-card" href="https://microsoftedge.microsoft.com/addons/detail/xsurf-log/hkippnibankfefilphlobakcabmplobj?hl=eng" target="_blank">
                  <i class="fa-brands fa-edge browser-icon" style="color: #0078D7;"></i>
                  <span class="browser-name">Microsoft Edge</span>
                  <span class="browser-sub">Edge Add-ons</span>
               </a>
            </div>
         </div>

         <!-- Getting Started -->
         <div class="section-card mb-4">
            <h5 class="section-title">
               <i class="fa-solid fa-rocket me-2"></i> Getting Started
            </h5>
            <p class="section-desc">After installing the extension, follow these steps to connect it to your XSurfLog account.</p>

            <div class="steps-container">
               <div class="step-row">
                  <span class="step-num">1</span>
                  <div>
                     <strong>Open the Extension</strong>
                     <p>Click the XSurfLog icon in your browser's toolbar. If you don't see it, click the puzzle piece icon to find it in your extensions list and pin it.</p>
                  </div>
               </div>
               <div class="step-row">
                  <span class="step-num">2</span>
                  <div>
                     <strong>Log In</strong>
                     <p>Enter your XSurfLog credentials. If your organisation uses Single Sign-On (SSO), click the SSO button instead.</p>
                  </div>
               </div>
               <div class="step-row">
                  <span class="step-num">3</span>
                  <div>
                     <strong>Extension Key Allocation</strong>
                     <p>Once logged in, the extension will automatically generate and register an extension key with your account. You can view allocated keys under <strong>Configuration → Extension Keys</strong>.</p>
                  </div>
               </div>
               <div class="step-row">
                  <span class="step-num">4</span>
                  <div>
                     <strong>Verify it's Working</strong>
                     <p>Visit any website and check your <strong>Dashboard → Log History</strong> to confirm browsing activity is being recorded.</p>
                  </div>
               </div>
            </div>
         </div>

         <!-- Accordion Sections -->
         <div class="section-card mb-3">
            <div class="accordion" id="installAccordion">

               <!-- Group Policy -->
               <div class="accordion-item">
                  <h2 class="accordion-header">
                     <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseGroupPolicy">
                        <i class="fa-solid fa-shield-halved me-2" style="color: #00A0C1;"></i>
                        Deploy via Group Policy (Enterprise)
                     </button>
                  </h2>
                  <div id="collapseGroupPolicy" class="accordion-collapse collapse" data-bs-parent="#installAccordion">
                     <div class="accordion-body">
                        <GroupPolicy />
                     </div>
                  </div>
               </div>

               <!-- Auto Install -->
               <div class="accordion-item">
                  <h2 class="accordion-header">
                     <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseAutoInstall">
                        <i class="fa-solid fa-wand-magic-sparkles me-2" style="color: #00A0C1;"></i>
                        Auto-Install Apps &amp; Extensions (Google Admin)
                     </button>
                  </h2>
                  <div id="collapseAutoInstall" class="accordion-collapse collapse" data-bs-parent="#installAccordion">
                     <div class="accordion-body">
                        <AppsAndExtensions />
                     </div>
                  </div>
               </div>

               <!-- FAQs -->
               <div class="accordion-item">
                  <h2 class="accordion-header">
                     <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFAQ">
                        <i class="fa-solid fa-circle-question me-2" style="color: #00A0C1;"></i>
                        Frequently Asked Questions
                     </button>
                  </h2>
                  <div id="collapseFAQ" class="accordion-collapse collapse" data-bs-parent="#installAccordion">
                     <div class="accordion-body">
                        <FAQs />
                     </div>
                  </div>
               </div>

            </div>
         </div>

      </div>
   </div>
</template>

<script>
import GroupPolicy from '@/components/ExtensionConfiguration/InstallationGuide/GroupPolicy.vue';
import AppsAndExtensions from '@/components/ExtensionConfiguration/InstallationGuide/AppsAndExtensions.vue';
import FAQs from '@/components/ExtensionConfiguration/InstallationGuide/FAQs.vue';

export default {
   components: {
      GroupPolicy,
      AppsAndExtensions,
      FAQs
   },
}
</script>

<style scoped>
.card { border: none; }

.section-card {
   background: #fff;
   border: 1px solid #e9ecef;
   border-radius: 12px;
   padding: 24px;
}

.section-title {
   font-weight: 700;
   font-size: 1.1rem;
   color: #435666;
   margin-bottom: 4px;
}
.section-title i { color: #00A0C1; }

.section-desc {
   font-size: 14px;
   color: #6c757d;
   margin-bottom: 20px;
   line-height: 1.6;
}

/* Browser Cards */
.browser-grid {
   display: flex;
   gap: 16px;
   flex-wrap: wrap;
}

.browser-card {
   display: flex;
   flex-direction: column;
   align-items: center;
   padding: 20px 28px;
   border: 1px solid #e9ecef;
   border-radius: 12px;
   text-decoration: none;
   transition: all 0.2s ease;
   min-width: 160px;
   flex: 1;
}
.browser-card:hover {
   border-color: #00A0C1;
   box-shadow: 0 4px 12px rgba(0,160,193,0.12);
   transform: translateY(-2px);
}

.browser-icon { font-size: 36px; margin-bottom: 10px; }
.browser-name { font-weight: 600; font-size: 14px; color: #435666; }
.browser-sub { font-size: 11px; color: #adb5bd; margin-top: 2px; }

/* Steps */
.steps-container {
   display: flex;
   flex-direction: column;
   gap: 16px;
}

.step-row {
   display: flex;
   gap: 14px;
   align-items: flex-start;
}

.step-num {
   flex-shrink: 0;
   width: 32px; height: 32px;
   border-radius: 50%;
   background: #00A0C1;
   color: #fff;
   font-size: 14px; font-weight: 700;
   display: flex; align-items: center; justify-content: center;
}

.step-row strong {
   font-size: 14px;
   color: #435666;
}

.step-row p {
   font-size: 13px;
   color: #6c757d;
   line-height: 1.6;
   margin: 4px 0 0;
}

/* Accordion */
.accordion-item {
   border: 1px solid #e9ecef;
   border-radius: 10px !important;
   margin-bottom: 8px;
   overflow: hidden;
}

.accordion-button {
   font-weight: 600;
   font-size: 15px;
   color: #435666;
   padding: 16px 20px;
}
.accordion-button:not(.collapsed) {
   background: #f5f6f8;
   color: #435666;
   box-shadow: none;
}
.accordion-button:focus { box-shadow: none; }

.accordion-body {
   font-size: 14px;
   color: #435666;
   line-height: 1.7;
   padding: 20px;
}
</style>
